import axios from 'axios';

// eslint-disable-next-line no-undef
const baseUrl = process.env.REACT_APP_BASEURL || 'https://cedars-api.herokuapp.com/api/v1';
// const baseUrl = 'http://192.168.8.100:8000';

axios.defaults.baseURL = baseUrl;

const apiInstance = axios.create();

apiInstance.interceptors.request.use(
  async config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = 'Bearer ' + token;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// The API answers some "not allowed" checks with 401 as well as an expired or bad token, so a 401
// alone doesn't mean the user is signed out. Check the session once (shared by requests failing
// together) and sign out only if the token itself is rejected.
let sessionCheck = null; // { token, result }
const sessionIsValid = () => {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve(false);
  if (sessionCheck?.token !== token) {
    const check = {
      token,
      result: axios
        .get('/auth/profile', { headers: { Authorization: 'Bearer ' + token } })
        .then(() => true)
        .catch((e) => e.response?.status !== 401)
        .finally(() => setTimeout(() => { if (sessionCheck === check) sessionCheck = null; }, 5000)),
    };
    sessionCheck = check;
  }
  return sessionCheck.result;
};

/** Event the layout listens to, to show the "you don't have permission" banner. */
export const FORBIDDEN_EVENT = 'app:forbidden';
const announceForbidden = (error) => {
  // Only for actions (create, update, delete); pages handle their own failed loads.
  if ((error.config?.method || 'get').toLowerCase() === 'get') return;
  window.dispatchEvent(new CustomEvent(FORBIDDEN_EVENT, { detail: { message: error.response?.data?.message || error.response?.data?.error } }));
};

apiInstance.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const status = error.response?.status;
    // No token (e.g. a wrong password on the login page): nothing to sign out of.
    if (status === 401 && !localStorage.getItem('token')) return Promise.reject(error);
    if (status === 401) {
      if (!(await sessionIsValid())) {
        localStorage.setItem('token', '');
        window.location.reload();
        return Promise.reject(error);
      }
      announceForbidden(error);
    } else if (status === 403) {
      announceForbidden(error);
    }
    return Promise.reject(error);
  }
);

export default apiInstance;

// ---------------------------------------------------------------- response cache
//
// GET responses are kept in memory for a few minutes so moving between pages (and the overviews,
// dashboard and reports asking for the same lists) doesn't refetch. Identical requests in flight
// are shared. Any create/update/delete clears the whole cache, so edits show straight away.
// Nothing is written to disk. Pass `{ cache: false }` in the request config to always fetch.
const DEFAULT_TTL = 3 * 60 * 1000;
const TTL_RULES = [
  // Live data and auth are never cached.
  { test: /^\/auth|\/active\/stats/, ttl: 0 },
  // Check-in lists and per-service stats are the expensive calls (one per service).
  { test: /^\/attendances|^\/services\/stats/, ttl: 10 * 60 * 1000 },
];
const responseCache = new Map();

const ttlFor = (url) => {
  const rule = TTL_RULES.find((r) => r.test.test(url));
  return rule ? rule.ttl : DEFAULT_TTL;
};

// Each caller gets its own copy, so one view sorting or editing a list can't change another's.
const copy = (data) =>
  typeof window.structuredClone === 'function' ? window.structuredClone(data) : JSON.parse(JSON.stringify(data));

export const clearApiCache = () => responseCache.clear();

const rawGet = apiInstance.get.bind(apiInstance);
apiInstance.get = (url, config = {}) => {
  const { cache = true, ...rest } = config;
  const ttl = ttlFor(url);
  if (!cache || !ttl) return rawGet(url, rest);

  // The token is part of the key, so a different signed-in user never sees another's responses.
  const key = `${localStorage.getItem('token') || ''}|${url}|${JSON.stringify(rest.params || {})}`;
  const hit = responseCache.get(key);
  if (!hit || Date.now() - hit.at > ttl) {
    const promise = rawGet(url, rest).catch((error) => {
      responseCache.delete(key); // failures aren't cached
      throw error;
    });
    responseCache.set(key, { at: Date.now(), promise });
  }
  return responseCache.get(key).promise.then((res) => ({ ...res, data: copy(res.data) }));
};

['post', 'put', 'patch', 'delete'].forEach((method) => {
  const raw = apiInstance[method].bind(apiInstance);
  apiInstance[method] = (...args) => raw(...args).finally(clearApiCache);
});
